export type UploadMediaType = 'image' | 'video' | 'audio'

const MEBIBYTE = 1024 * 1024

/** Browser-side upload limits. Keep the API's defensive limits in sync. */
export const MAX_FILE_SIZE: Readonly<Record<UploadMediaType, number>> = {
  image: 10 * MEBIBYTE,
  video: 300 * MEBIBYTE,
  audio: 50 * MEBIBYTE,
}

export type UploadFileSizeError = {
  code: 'upload.tooLarge'
  params: { type: string; maxMb: number }
}

function isUploadMediaType(value: string): value is UploadMediaType {
  return value === 'image' || value === 'video' || value === 'audio'
}

export function uploadMediaTypeFromMime(mimeType: string): UploadMediaType | null {
  const category = mimeType.split('/', 1)[0]
  return isUploadMediaType(category) ? category : null
}

export function getFileSizeError(
  fileSize: number,
  mediaType: string,
): UploadFileSizeError | null {
  const maxSize = isUploadMediaType(mediaType) ? MAX_FILE_SIZE[mediaType] : MAX_FILE_SIZE.image
  if (fileSize > maxSize) {
    return {
      code: 'upload.tooLarge',
      params: { type: mediaType, maxMb: maxSize / MEBIBYTE },
    }
  }
  return null
}

/** Native `accept` value for each media family. */
export const MEDIA_ACCEPT_BY_TYPE: Readonly<Record<UploadMediaType, string>> = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
}

export interface ClassifiedMediaFile {
  file: File
  type: UploadMediaType
}

export interface MediaFileClassification {
  /** Supported files, in the order they were selected/dropped. */
  accepted: ClassifiedMediaFile[]
  /** Files that are not image/video/audio. Callers must report these. */
  rejected: File[]
}

/**
 * Split a multi-file selection or drop into supported media and the rest.
 *
 * Shared by the merged "Upload media" picker and canvas drag-and-drop so both
 * agree on what "supported" means. Only the MIME family is checked here;
 * per-file size limits stay with the consumer that owns the upload.
 */
export function classifyMediaFiles(files: Iterable<File>): MediaFileClassification {
  const accepted: ClassifiedMediaFile[] = []
  const rejected: File[] = []
  for (const file of files) {
    const type = uploadMediaTypeFromMime(file.type)
    if (type) accepted.push({ file, type })
    else rejected.push(file)
  }
  return { accepted, rejected }
}

/** `name:type` list for NAD logs, e.g. `hero.png:image,clip.mp4:video`. */
export function describeMediaFiles(files: readonly ClassifiedMediaFile[]): string {
  return files.map(({ file, type }) => `${file.name}:${type}`).join(',')
}
